import { ElMessage } from 'element-plus';
import appConfig from '../config/app.config.js';

// 使用全局的io对象（通过CDN加载）
const io = window.io;

class WebSocketClient {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.eventHandlers = new Map();
  }

  // 连接WebSocket
  connect(token) {
    if (this.socket && this.isConnected) {
      console.log('WebSocket已连接');
      return;
    }

    try {
      // 使用配置文件中的WebSocket地址
      const serverUrl = appConfig.getWebSocketUrl();
      
      // 打印当前配置（调试用）
      appConfig.printConfig();
      console.log('WebSocket连接地址:', serverUrl);

      this.socket = io(serverUrl, {
        auth: { token },
        transports: ['websocket', 'polling'],
        timeout: 20000,
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: this.reconnectDelay,
        forceNew: true
      });

      this.setupEventHandlers();
      console.log('WebSocket连接中...');
    } catch (error) {
      console.error('WebSocket连接失败:', error);
      ElMessage.error('实时通讯连接失败');
    }
  }

  // 设置事件处理器
  setupEventHandlers() {
    if (!this.socket) return;

    // 连接成功
    this.socket.on('connect', () => {
      console.log('✅ WebSocket连接成功');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.emit('connected');
    });

    // 连接断开
    this.socket.on('disconnect', (reason) => {
      console.log('WebSocket连接断开:', reason);
      this.isConnected = false;
      this.emit('disconnected', reason);
      
      if (reason === 'io server disconnect') {
        // 服务器主动断开，尝试重连
        this.socket.connect();
      }
    });

    // 连接错误
    this.socket.on('connect_error', (error) => {
      console.error('❌ WebSocket连接错误:', error);
      this.reconnectAttempts++;
      
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('WebSocket重连失败，已达到最大重试次数');
        ElMessage.error('实时通讯连接失败，请刷新页面重试');
      }
      
      this.emit('error', error);
    });

    // 重连尝试
    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`WebSocket重连尝试 ${attemptNumber}/${this.maxReconnectAttempts}`);
      this.emit('reconnect_attempt', attemptNumber);
    });

    // 重连成功
    this.socket.on('reconnect', (attemptNumber) => {
      console.log(`WebSocket重连成功，尝试次数: ${attemptNumber}`);
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.emit('reconnected', attemptNumber);
    });

    // 重连失败
    this.socket.on('reconnect_failed', () => {
      console.error('WebSocket重连失败');
      ElMessage.error('实时通讯连接失败，请检查网络连接');
      this.emit('reconnect_failed');
    });

    // 加入申请房间成功
    this.socket.on('joined_application', (data) => {
      console.log('已加入申请房间:', data.applicationId);
      this.emit('joined_application', data);
    });

    // 离开申请房间成功
    this.socket.on('left_application', (data) => {
      console.log('已离开申请房间:', data.applicationId);
      this.emit('left_application', data);
    });

    // 收到新消息
    this.socket.on('new_message', (data) => {
      console.log('收到新消息:', data);
      this.emit('new_message', data);
    });

    // 消息发送成功
    this.socket.on('message_sent', (data) => {
      console.log('消息发送成功:', data);
      this.emit('message_sent', data);
    });

    // 消息已读
    this.socket.on('messages_read', (data) => {
      console.log('消息已读:', data);
      this.emit('messages_read', data);
    });

    // 错误消息
    this.socket.on('error', (data) => {
      console.error('WebSocket错误:', data);
      ElMessage.error(data.message || '操作失败');
      this.emit('error', data);
    });

    // 权限变更通知
    this.socket.on('permission_changed', (data) => {
      console.log('📢 收到权限变更通知:', data);
      this.emit('permission_changed', data);
    });
  }

  // 加入申请房间
  joinApplication(applicationId) {
    if (!this.socket || !this.isConnected) {
      console.error('WebSocket未连接');
      return;
    }

    this.socket.emit('join_application', applicationId);
  }

  // 离开申请房间
  leaveApplication(applicationId) {
    if (!this.socket || !this.isConnected) {
      return;
    }

    this.socket.emit('leave_application', applicationId);
  }

  // 发送消息
  sendMessage(applicationId, message) {
    if (!this.socket || !this.isConnected) {
      console.error('WebSocket未连接');
      return Promise.reject(new Error('WebSocket未连接'));
    }

    return new Promise((resolve, reject) => {
      // 设置超时
      const timeout = setTimeout(() => {
        reject(new Error('发送消息超时'));
      }, 10000);

      // 监听发送成功事件
      const onMessageSent = (data) => {
        clearTimeout(timeout);
        this.off('message_sent', onMessageSent);
        resolve(data);
      };

      // 监听错误事件
      const onError = (error) => {
        clearTimeout(timeout);
        this.off('error', onError);
        reject(error);
      };

      this.on('message_sent', onMessageSent);
      this.on('error', onError);

      // 发送消息
      this.socket.emit('send_message', { applicationId, message });
    });
  }

  // 标记消息已读
  markMessagesAsRead(applicationId) {
    if (!this.socket || !this.isConnected) {
      return;
    }

    this.socket.emit('mark_read', { applicationId });
  }

  // 断开连接
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      console.log('WebSocket已断开连接');
    }
  }

  // 事件监听
  on(event, handler) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event).push(handler);
  }

  // 移除事件监听
  off(event, handler) {
    if (this.eventHandlers.has(event)) {
      const handlers = this.eventHandlers.get(event);
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  // 触发事件
  emit(event, data) {
    if (this.eventHandlers.has(event)) {
      this.eventHandlers.get(event).forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`事件处理器错误 (${event}):`, error);
        }
      });
    }
  }

  // 获取连接状态
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts
    };
  }
}

// 创建单例实例
const websocketClient = new WebSocketClient();

export default websocketClient; 